import React from 'react';
import LocationHierarchy from './LocationHierarchy';

interface BoxCardProps {
  box: any;
  onEdit: (box: any) => void;
  onDelete: (box: any) => void;
  isSelected?: boolean;
  onToggleSelection?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  canBulk?: boolean;
}

const BoxCard: React.FC<BoxCardProps> = ({ 
  box, 
  onEdit, 
  onDelete, 
  isSelected = false, 
  onToggleSelection,
  canEdit = false,
  canDelete = false,
  canBulk = false
}) => {
  const getLocationData = () => {
    // Une box peut être attachée à une pièce OU à une zone
    if (box.zone) {
      return {
        site: box.zone.piece?.batiment?.site,
        batiment: box.zone.piece?.batiment,
        piece: box.zone.piece,
        zone: box.zone,
        box: box
      };
    } else if (box.piece) {
      return {
        site: box.piece.batiment?.site,
        batiment: box.piece.batiment,
        piece: box.piece,
        zone: null,
        box: box
      };
    }
    return null;
  };

  const getStatusInfo = () => {
    const isAtHome = box.currentZoneId === box.homeZoneId;
    const isAway = box.currentZoneId && box.currentZoneId !== box.homeZoneId;
    
    if (isAtHome) {
      return { text: '🏠 Au domicile', color: 'text-green-600' };
    } else if (isAway) {
      return { text: '🚚 Hors domicile', color: 'text-orange-600' };
    }
    return { text: '📍 Position inconnue', color: 'text-gray-600' };
  };

  const status = getStatusInfo();

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
      {/* En-tête avec case à cocher, code, thème et actions */}
      <div className="flex items-start gap-2 mb-2">
        {/* Case à cocher à gauche */}
        {onToggleSelection && canBulk && (
          <label className="flex-shrink-0 cursor-pointer mt-0.5">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelection}
              className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </label>
        )}
        
        {/* Contenu principal */}
        <div className="flex items-start justify-between gap-2 flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm truncate">{box.code}</h3>
            {box.theme && (
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-800 flex-shrink-0">
                {box.theme}
              </span>
            )}
          </div>
          {(canEdit || canDelete) && (
            <div className="flex gap-1 flex-shrink-0">
              {canEdit && (
                <button
                  onClick={() => onEdit(box)}
                  className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                  title="Modifier"
                >
                  ✏️
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => onDelete(box)}
                  className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                  title="Supprimer"
                >
                  🗑️
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Nom + Statut */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-sm text-gray-700 font-medium truncate flex-1">{box.nom}</p>
        <span className={`text-xs font-medium flex-shrink-0 ${status.color}`}>
          {status.text.split(' ')[0]}
        </span>
      </div>

      {/* Localisation - Version verticale avec icônes */}
      <div className="space-y-0.5 mb-2">
        {box.piece?.batiment?.site && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="flex-shrink-0">📍</span>
            <span className="truncate">{box.piece.batiment.site.nom}</span>
          </div>
        )}
        {box.piece?.batiment && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="flex-shrink-0">🏗️</span>
            <span className="truncate">{box.piece.batiment.nom}</span>
          </div>
        )}
        {box.piece && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="flex-shrink-0">🚪</span>
            <span className="truncate">{box.piece.nom}</span>
          </div>
        )}
        {box.zone && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="flex-shrink-0">🏠</span>
            <span className="truncate">{box.zone.nom}</span>
          </div>
        )}
      </div>

      {/* Statistiques */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <span>📦</span>
          <span>{box.objets?.length || 0} objet(s)</span>
        </span>
        {box.homeZone && (
          <span className="flex items-center gap-1 truncate">
            <span>🏠</span>
            <span className="truncate">{box.homeZone.code}</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default BoxCard;
